import LanguageSchema from "@/locales/LanguageSchema";
import Language from "@/types/Models/Settings/Language";
import {Composer, createI18n, I18n} from "vue-i18n";

/**
 * Translator class responsible for initializing and managing the i18n (internationalization) library instance.
 * This singleton class provides functionality for setting the application's language, accessing i18n instances,
 * and handling global translations.
 */
class Translator {
    /** Singleton instance of the Translator */
    private static instance: Translator;

    /** Vue-i18n instance configured for managing localization and translation messages */
    private i18n!: I18n;

    /**
     * Initializes a new Translator instance.
     * This constructor is private to enforce the singleton pattern.
     */
    private constructor() {
        this.init();
    }

    /**
     * Initializes the i18n library with specified settings.
     * Configures the default locale, fallback locale, and loads the locale messages from `LanguageSchema`.
     * This method is automatically called during instantiation to set up i18n.
     *
     * @remarks
     * This function configures i18n to use a composition API, disables warnings for HTML in messages,
     * and enables global injection of translation methods.
     */
    private init(): void {
        //@ts-ignore
        this.i18n = createI18n<[Language], "de" | "en", false>({
            legacy: false,
            locale: "de", // Set default locale
            fallbackLocale: "de", // Set fallback locale for missing translations
            messages: LanguageSchema, // Load translations from predefined schema
            warnHtmlInMessage: "off",
            warnHtmlMessage: false,
            globalInjection: true,
        });
    }

    /**
     * Returns the main i18n instance.
     * Allows access to the i18n configuration and properties for managing global translation state.
     *
     * @returns {I18n} The initialized i18n instance.
     */
    public getI18nInstance(): I18n {
        return this.i18n;
    }

    /**
     * Returns the global i18n Composer instance, used for direct access to the `global` scope of translations.
     * The global Composer instance provides access to all messages and translation functions.
     *
     * @returns {Composer<{ message: Language }, Record<"en" | "de", any>>}
     * The i18n Composer instance configured with messages of type `Language`.
     */
    public getI18nGlobalInstance(): Composer<{ message: Language }, Record<"en" | "de", any>> {
        return this.i18n.global as unknown as Composer<{ message: Language }, Record<"en" | "de", any>>;
    }

    /**
     * Sets the active language of the i18n instance.
     * Adjusts the current locale dynamically, either for the legacy or non-legacy i18n mode.
     *
     * @param {string} locale - The locale code to set as active (e.g., `"en"` or `"de"`).
     * @remarks
     * This method supports both legacy and composition i18n API modes. For composition mode, it updates
     * the `locale` property directly, ensuring compatibility with different i18n configurations.
     */
    public setLanguage(locale: string): void {
        if (this.i18n.mode === "legacy") {
            this.i18n.global.locale = locale;
        } else {
            (this.i18n.global.locale as any).value = locale;
        }
    }

    /**
     * Retrieves the singleton instance of the Translator.
     * Ensures that only one instance of Translator exists at any given time.
     *
     * @returns {Translator} The single Translator instance.
     *
     * @remarks
     * This method creates the Translator instance if it does not exist yet. Otherwise, it returns the
     * existing instance, following the singleton design pattern.
     */
    public static getInstance(): Translator {
        if (!Translator.instance) {
            Translator.instance = new Translator();
        }
        return Translator.instance;
    }
}

export default Translator;
